/**
 * The staff module's constant values, mirroring `pkg/enums/*` on the API side
 * (docs/OSEBJE.md §5, §7, §11).
 *
 * They are written out here rather than read from the wire because every one of them
 * needs a Slovenian word beside it, and a label assembled from a wire name is a key
 * `yarn check-i18n` cannot see — CLAUDE.md deviation 10, and the reason that check
 * exists.
 */

/** Whether the people named on a shift have been told about it (docs/OSEBJE.md §7). */
export enum StaffShiftState {
    Draft     = 'draft',
    Published = 'published',

    /**
     * A published shift withdrawn. The interval is kept and the reason recorded — a
     * deleted one would take with it the trace of what was promised (§12).
     */
    Cancelled = 'cancelled',
}

/**
 * How far the notice about a CHANGED published shift got (docs/OSEBJE.md §7).
 *
 * The distinction the whole enum exists for: "we sent it" and "she was told" are not
 * the same sentence, and only the second one settles anything when somebody turns up at
 * the wrong hour.
 *
 * In this phase the delivery is IN THE APPLICATION — the person sees the change on
 * their own rota and presses "I understand" — so `Sent` never appears: nothing is sent,
 * because an account carries no e-mail address. It is here because the API's enum has
 * it, and because it becomes true the day a mail sender is built.
 */
export enum StaffNotificationState {
    Pending   = 'pending',
    Sent      = 'sent',
    Delivered = 'delivered',
    Failed    = 'failed',
}

/**
 * HOW a change to a published shift came to be taken in.
 *
 * Kept apart from the delivery state above because they answer different questions and
 * one of the answers is weaker. `Self` is the person's own press on their own rota —
 * their own hand, and the only thing that may write `delivered`. `InPerson` is a
 * supervisor writing down that she told them at the handover, which is the delivery that
 * actually happens when a notice fails or the person carries no channel at all
 * (docs/OBVESCANJE.md §5).
 *
 * `Declined` is on the same field and is NOT one of them: „Ne morem“, answered on the
 * WhatsApp buttons or in the person's own words (§10.1). It settles nothing — a shift
 * somebody cannot work is a hole to fill — so a declined change stays on both lists and
 * says which of the two it is.
 */
export enum StaffAcknowledgement {
    Self     = 'self',
    InPerson = 'in_person',
    Declined = 'declined',
}

/**
 * The legal ground somebody works on. Not only employment: ZEPDSV-B covers every
 * ground, and half a hotel's summer is not in an employment relationship at all
 * (docs/OSEBJE.md §5).
 */
export enum EngagementBasis {
    EmploymentContract  = 'employment_contract',
    AgencyWorker        = 'agency_worker',
    StudentWork         = 'student_work',
    PensionerTemporary  = 'pensioner_temporary',
    Traineeship         = 'traineeship',
    ServiceContract     = 'service_contract',
    CopyrightContract   = 'copyright_contract',
    ShortTermFamilyWork = 'short_term_family_work',
    SelfEmployed        = 'self_employed',
    Other               = 'other',
}

/** A contract of employment's own terms, and nothing else's (docs/OSEBJE.md §5). */
export enum ContractDuration {
    Indefinite = 'indefinite',
    FixedTerm  = 'fixed_term',
}

export enum WorkingTimeType {
    FullTime = 'full_time',
    PartTime = 'part_time',
}

/** What the working-time record is kept FROM. */
export enum TimeRecordBasis {
    Clock    = 'clock',
    Schedule = 'schedule',
    Pattern  = 'pattern',
    Manual   = 'manual',
}

/**
 * Why there is no compulsory record. A managing person lives here and not among the
 * legal grounds above: a director may hold an ordinary contract of employment, and
 * folding the exemption into the ground would make that person unwritable (§5).
 */
export enum TimeRecordExemption {
    ManagingPerson = 'managing_person',
    Other          = 'other',
}

/** The modules a licence can carry, for the ones this portal has to ask about. */
export enum StaffModule {
    /**
     * "Zaposleni in čas" — the workforce module (docs/OSEBJE.md §14). Everything in
     * this portal belongs to it, so an unlicensed house has no portal at all rather
     * than a portal with an empty screen in it. Asked for once per session, because a
     * `staff` route in a house without the licence answers 403 with code 19 and the
     * whole application then draws ModuleNotice (libs/license.ts).
     */
    Staff = 'staff',
}
